import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../components/DashboardLayout";
import AddConnectorDialog from "./components/AddConnectorDialog";
import DeleteConnectionDialog from "./components/DeleteConnectionDialog";
import MCPDetailDrawer from "./components/MCPDetailDrawer";
import MCPList from "./components/MCPList";
import MCPToolbar from "./components/MCPToolbar";
import { mapDtoToFormValues, mapFormToCreatePayload } from "./adapters";
import {
  openOAuthPopup,
  settleOAuthPopup,
  watchOAuthPopupCompletion,
} from "./oauth";
import { useCreateMCPConnection } from "./hooks/useCreateMCPConnection";
import { useDeleteMCPConnection } from "./hooks/useDeleteMCPConnection";
import { useMCPConnection } from "./hooks/useMCPConnection";
import {
  MCP_CONNECTIONS_QUERY_KEY,
  useMCPConnections,
} from "./hooks/useMCPConnections";
import { useUpdateMCPConnection } from "./hooks/useUpdateMCPConnection";
import { useUsers } from "./hooks/useUsers";
import type { ConnectorFormValues } from "./types";
import { canManageMcpConnection } from "./connectionAccess";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../contexts/AuthContext";

// The page has exactly one "active surface" at a time. Keeping that in the URL
// gives us:
//   - a single source of truth (no juggling 4 useStates)
//   - back-button + shareable links for free
//   - mutual exclusion by construction (no two overlays fighting)
const VIEW_VALUES = ["drawer", "edit", "create", "delete"] as const;
type View = (typeof VIEW_VALUES)[number];

const MCPsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { connections, isLoading, error } = useMCPConnections();
  const { data: users = [] } = useUsers();
  const { userProfile, currentUser } = useAuth();

  const selfId: string | undefined = userProfile?.uid ?? currentUser?.id;
  const selfEmail: string | undefined = (
    userProfile?.email ?? currentUser?.email
  )?.toLowerCase();
  const currentOrgUser = useMemo(() => {
    const byEmail = selfEmail
      ? users.find((user) => user.email?.toLowerCase() === selfEmail)
      : undefined;
    return byEmail ?? users.find((user) => selfId && user.id === selfId);
  }, [selfEmail, selfId, users]);

  const manageableConnectionIds = useMemo(
    () =>
      new Set(
        connections
          .filter((connection) =>
            canManageMcpConnection(connection.sharing, currentOrgUser),
          )
          .map((connection) => connection.id),
      ),
    [connections, currentOrgUser],
  );
  const canManage = useCallback(
    (connectionId: string) => manageableConnectionIds.has(connectionId),
    [manageableConnectionIds],
  );

  const [query, setQuery] = useQueryState("q", parseAsString.withDefault(""));
  const [{ view, id }, setUrlState] = useQueryStates({
    view: parseAsStringLiteral(VIEW_VALUES),
    id: parseAsString,
  });

  // Derive every surface's state from the URL. No local copies.
  const selectedId = view === "drawer" ? id : null;
  const editingId = view === "edit" ? id : null;
  const pendingDeleteId = view === "delete" ? id : null;
  const isAddOpen = view === "create";
  const authorizedEditingId = editingId && canManage(editingId) ? editingId : null;
  const dialogOpen = view === "create" || Boolean(authorizedEditingId);
  const dialogMode: "create" | "edit" =
    view === "edit" ? "edit" : "create";

  // Pulls the row from the shared cache so the dialog can pre-fill instantly
  // when the user picks Edit from the list menu.
  const { dto: editingDto } = useMCPConnection(authorizedEditingId);

  const closeAll = useCallback(() => {
    setUrlState({ view: null, id: null });
  }, [setUrlState]);

  const openView = useCallback(
    (next: View, targetId: string | null = null) => {
      setUrlState({ view: next, id: targetId });
    },
    [setUrlState],
  );

  const createMutation = useCreateMCPConnection({
    onSuccess: () => {
      if (isAddOpen) closeAll();
    },
  });
  const updateMutation = useUpdateMCPConnection({
    onSuccess: () => {
      if (view === "edit") closeAll();
    },
  });
  const deleteMutation = useDeleteMCPConnection({
    onSuccess: (deletedId) => {
      // If anything else was pointing at the same row, close it.
      if (id === deletedId) closeAll();
    },
  });

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return connections;
    return connections.filter((connection) =>
      connection.name.toLowerCase().includes(trimmed),
    );
  }, [query, connections]);

  const pendingDeleteConnection = useMemo(
    () =>
      pendingDeleteId
        ? connections.find(
            (connection) =>
              connection.id === pendingDeleteId && canManage(connection.id),
          ) ?? null
        : null,
    [pendingDeleteId, connections, canManage],
  );

  const editInitialValues = useMemo(() => {
    if (!editingDto) return undefined;
    try {
      return mapDtoToFormValues(editingDto);
    } catch (err) {
      console.error("[MCPs] mapDtoToFormValues threw", err, "dto:", editingDto);
      return undefined;
    }
  }, [editingDto]);

  const dialogSubmitting =
    dialogMode === "edit"
      ? updateMutation.isPending
      : createMutation.isPending;

  const handleSelect = useCallback(
    (rowId: string) => openView("drawer", rowId),
    [openView],
  );
  const handleEdit = useCallback(
    (rowId: string) => {
      if (canManage(rowId)) openView("edit", rowId);
    },
    [canManage, openView],
  );
  const handleDelete = useCallback(
    (rowId: string) => {
      if (canManage(rowId)) openView("delete", rowId);
    },
    [canManage, openView],
  );
  const handleNewConnection = useCallback(
    () => openView("create"),
    [openView],
  );

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId && canManage(pendingDeleteId)) {
      deleteMutation.mutate(pendingDeleteId);
    }
  }, [pendingDeleteId, canManage, deleteMutation]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) closeAll();
    },
    [closeAll],
  );

  const handleDialogSubmit = useCallback(
    (values: ConnectorFormValues) => {
      const payload = mapFormToCreatePayload(values);
      if (dialogMode === "edit" && authorizedEditingId) {
        updateMutation.mutate({ id: authorizedEditingId, payload });
        return;
      }

      // An unauthorized edit URL must never fall through into create.
      if (dialogMode === "edit") return;

      // Pre-open the OAuth tab synchronously inside the submit click so the
      // browser allows it (blockers reject window.open after the await).
      const popup = values.authHeader === "oauth" ? openOAuthPopup() : null;

      createMutation.mutate(payload, {
        onSuccess: (response) => {
          const authorizationUrl = response.oauth_init?.authorization_url;
          const { redirected, blocked } = settleOAuthPopup(
            popup,
            authorizationUrl,
          );
          if (redirected) {
            watchOAuthPopupCompletion(popup!, authorizationUrl!, () => {
              queryClient.invalidateQueries({
                queryKey: MCP_CONNECTIONS_QUERY_KEY,
              });
            });
            toast.success(
              t("mcpPage.toasts.addedAuthorize"),
            );
          } else if (blocked && authorizationUrl) {
            toast.warning(
              t("mcpPage.toasts.popupBlocked", { url: authorizationUrl }),
            );
          }
        },
        onError: () => {
          popup?.close();
        },
      });
    },
    [
      dialogMode,
      authorizedEditingId,
      updateMutation,
      createMutation,
      queryClient,
      t,
    ],
  );

  return (
    <DashboardLayout title={t("mcpPage.headerTitle")}>
      <div className="p-2 sm:p-4">
        <MCPToolbar
          query={query}
          onQueryChange={setQuery}
          onNewConnection={handleNewConnection}
        />
        <MCPList
          connections={filtered}
          selectedId={selectedId}
          canManage={canManage}
          onSelect={handleSelect}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          errorMessage={error ? t("mcpPage.errors.loadConnections") : null}
        />
      </div>

      <MCPDetailDrawer
        open={Boolean(selectedId)}
        onClose={closeAll}
        connectionId={selectedId}
        currentUser={currentOrgUser}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <AddConnectorDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={editInitialValues}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
        isSubmitting={dialogSubmitting}
      />

      <DeleteConnectionDialog
        connection={pendingDeleteConnection}
        isDeleting={deleteMutation.isPending}
        onCancel={closeAll}
        onConfirm={handleConfirmDelete}
      />
    </DashboardLayout>
  );
};

export default MCPsPage;
